import { IDynamicFormFieldsJson } from "../../models/user/DynamicForm/DynamicForm";
import {
  DynamicFieldStatus,
  flattenDynamicFields,
  getNextDynamicFieldQuestion,
  getNextMissingDynamicFieldLabel,
} from "./voiceDynamicFormLogic";
import {
  ClaimField,
  getClaimFormEnquiryQuestions,
  TranscriptTurn,
} from "./voiceLiveDemoLogic";
import { VoiceFormIntentId } from "./voiceFormRegistry";

const QUESTION_START =
  /^(can|could|do|does|did|is|are|am|was|were|will|would|should|when|where|how|what|who|why)\b/i;

const AGENT_GREETING_QUESTIONS = [
  /^how can i help you today\??$/i,
];

export type CsrEnquiryKind = "form" | "customer";

export interface CsrEnquiryItem {
  question: string;
  kind: CsrEnquiryKind;
}

export interface CsrAssistInput {
  transcript: TranscriptTurn[];
  activeFormId: VoiceFormIntentId | null;
  claimFields?: Record<string, ClaimField>;
  dynamicFormSchema?: IDynamicFormFieldsJson | null;
  dynamicFieldStatuses?: Record<string, DynamicFieldStatus>;
}

function capitalize(s: string): string {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function normalizeQuestion(text: string): string {
  let question = text.trim().replace(/\s+/g, " ");
  if (!question) return "";
  if (!question.endsWith("?")) {
    question = `${question.replace(/[.!]+$/, "")}?`;
  }
  return capitalize(question);
}

function splitIntoSentences(text: string): string[] {
  return text
    .split(/(?<=[.!?])\s+/)
    .map((part) => part.trim())
    .filter(Boolean);
}

function isQuestionSentence(sentence: string): boolean {
  const trimmed = sentence.trim();
  if (!trimmed) return false;
  if (AGENT_GREETING_QUESTIONS.some((p) => p.test(trimmed))) return false;
  if (trimmed.endsWith("?")) return true;
  return QUESTION_START.test(trimmed);
}

function extractFromClaimantText(text: string): string[] {
  const found: string[] = [];
  const trimmed = text.trim();
  if (!trimmed) return found;

  for (const sentence of splitIntoSentences(trimmed)) {
    if (isQuestionSentence(sentence)) {
      found.push(normalizeQuestion(sentence));
    }
  }

  if (!found.length && trimmed.includes("?")) {
    const chunks = trimmed.split(/(?<=\?)/);
    for (const chunk of chunks) {
      const part = chunk.trim();
      if (!part || !part.includes("?")) continue;
      const questionOnly = part.split("?")[0]?.trim();
      if (questionOnly && isQuestionSentence(`${questionOnly}?`)) {
        found.push(normalizeQuestion(`${questionOnly}?`));
      }
    }
  }

  return found;
}

/** Questions the customer asked — claimant turns only, never agent/AI. */
export function extractClaimantQuestionsFromTranscript(
  transcript: TranscriptTurn[]
): string[] {
  const seen = new Set<string>();
  const questions: string[] = [];

  for (const turn of transcript) {
    if (turn.speaker !== "Claimant") continue;
    if (turn.streaming || !turn.text?.trim()) continue;
    for (const question of extractFromClaimantText(turn.text)) {
      const key = normalizeQuestionKey(question);
      if (!seen.has(key)) {
        seen.add(key);
        questions.push(question);
      }
    }
  }

  return questions;
}

export function getDynamicFormEnquiryQuestions(
  schema: IDynamicFormFieldsJson,
  statuses: Record<string, DynamicFieldStatus>
): string[] {
  const questions: string[] = [];
  const simulated: Record<string, DynamicFieldStatus> = { ...statuses };
  let guard = 0;

  while (guard < flattenDynamicFields(schema).length + 2) {
    const question = getNextDynamicFieldQuestion(schema, simulated);
    if (!question) break;

    const normalized = question.endsWith("?") ? question : `${question}?`;
    questions.push(normalized);

    const label = getNextMissingDynamicFieldLabel(schema, simulated);
    if (!label) break;

    const field = flattenDynamicFields(schema).find((f) => f.label === label);
    if (!field) break;

    simulated[field.name] = {
      label: field.label,
      value: "—",
      status: "complete",
      source: "csr-assist",
    };
    guard += 1;
  }

  return questions;
}

function buildFormEnquiries(input: CsrAssistInput): string[] {
  if (input.activeFormId === "file_claim") {
    return getClaimFormEnquiryQuestions(input.claimFields ?? {});
  }

  if (input.activeFormId && input.dynamicFormSchema) {
    return getDynamicFormEnquiryQuestions(
      input.dynamicFormSchema,
      input.dynamicFieldStatuses ?? {}
    );
  }

  return [];
}

/** Customer questions only (claimant speech) — no form intake prompts. */
export function buildCsrEnquiries(input: CsrAssistInput): CsrEnquiryItem[] {
  const seen = new Set<string>();
  const items: CsrEnquiryItem[] = [];

  for (const question of extractClaimantQuestionsFromTranscript(input.transcript)) {
    const key = normalizeQuestionKey(question);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    items.push({ question, kind: "customer" });
  }

  return items;
}

export function normalizeQuestionKey(question: string): string {
  return question.trim().toLowerCase().replace(/\s+/g, " ");
}

export function stableQuestionId(question: string, kind: CsrEnquiryKind): string {
  const key = `${kind}:${normalizeQuestionKey(question)}`;
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash << 5) - hash + key.charCodeAt(i);
    hash |= 0;
  }
  return `q-${Math.abs(hash).toString(16)}`;
}

export function formatTranscriptForAssist(
  transcript: TranscriptTurn[]
): string {
  return transcript
    .filter((turn) => !turn.streaming && turn.text?.trim())
    .map((turn) => `${turn.speaker}: ${turn.text.trim()}`)
    .join("\n");
}
